import React, { useEffect, useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { AppModal } from '../base/AppModal';
import { AppInput } from '../base/AppInput';
import { AppTextarea } from '../base/AppTextarea';
import { AppButton } from '../base/AppButton';
import type { Policy } from '../../types';
// ============================================================================
// POLICY EDIT MODAL
// ============================================================================
interface PolicyEditModalProps {
  isOpen: boolean;
  policy: Policy | null;
  onClose: () => void;
  onSave: (id: string, data: Partial<Policy>) => Promise<void>;
}
export function PolicyEditModal({
  isOpen,
  policy,
  onClose,
  onSave
}: PolicyEditModalProps) {
  const [title, setTitle] = useState(policy?.title || '');
  const [description, setDescription] = useState(policy?.description || '');
  const [category, setCategory] = useState<Policy['category']>(policy?.category || 'privacy');
  const [severity, setSeverity] = useState<Policy['severity']>(policy?.severity || 'medium');
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    if (policy) {
      setTitle(policy.title);
      setDescription(policy.description);
      setCategory(policy.category);
      setSeverity(policy.severity);
    }
  }, [policy]);
  const handleSave = async () => {
    if (!policy) return;
    setIsSaving(true);
    try {
      await onSave(policy.id, {
        title,
        description,
        category,
        severity
      });
      onClose();
    } catch (error) {
      console.error('Failed to save policy', error);
    } finally {
      setIsSaving(false);
    }
  };
  return <AppModal isOpen={isOpen} onClose={onClose} title="Modifica Policy" data-id="element-469">
      <div className="space-y-4" data-id="element-470">
        <AppInput label="Titolo" value={title} onChange={e => setTitle(e.target.value)} placeholder="Es. Nessun dato sensibile" data-id="element-471" />

        <AppTextarea label="Descrizione" value={description} onChange={e => setDescription(e.target.value)} placeholder="Descrivi la policy in dettaglio..." rows={4} data-id="element-472" />

        <div data-id="element-473">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-474">
            Categoria
          </label>
          <select value={category} onChange={e => setCategory(e.target.value as Policy['category'])} className="w-full bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle text-gray-700 dark:text-gray-300 py-2.5 px-3.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" data-id="element-475">
            <option value="privacy" data-id="element-476">Privacy</option>
            <option value="content" data-id="element-477">Contenuti</option>
            <option value="behavior" data-id="element-478">Comportamento</option>
            <option value="compliance" data-id="element-479">Compliance</option>
          </select>
        </div>

        <div data-id="element-480">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-481">
            Severità
          </label>
          <select value={severity} onChange={e => setSeverity(e.target.value as Policy['severity'])} className="w-full bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle text-gray-700 dark:text-gray-300 py-2.5 px-3.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" data-id="element-482">
            <option value="high" data-id="element-483">Alta</option>
            <option value="medium" data-id="element-484">Media</option>
            <option value="low" data-id="element-485">Bassa</option>
          </select>
        </div>

        <div className="flex gap-3 pt-4" data-id="element-486">
          <AppButton variant="secondary" onClick={onClose} fullWidth data-id="element-487">
            Annulla
          </AppButton>
          <AppButton onClick={handleSave} fullWidth disabled={isSaving || !title || !description} data-id="element-488">
            {isSaving ? 'Salvataggio...' : 'Salva modifiche'}
          </AppButton>
        </div>
      </div>
    </AppModal>;
}
// ============================================================================
// POLICY CREATE MODAL
// ============================================================================
interface PolicyCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: Omit<Policy, 'id'>) => Promise<void>;
}
export function PolicyCreateModal({
  isOpen,
  onClose,
  onCreate
}: PolicyCreateModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<Policy['category']>('privacy');
  const [severity, setSeverity] = useState<Policy['severity']>('medium');
  const [enabled, setEnabled] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const handleCreate = async () => {
    setIsCreating(true);
    try {
      await onCreate({
        title,
        description,
        category,
        severity,
        enabled
      });
      // Reset form
      setTitle('');
      setDescription('');
      setCategory('privacy');
      setSeverity('medium');
      setEnabled(true);
      onClose();
    } catch (error) {
      console.error('Failed to create policy', error);
    } finally {
      setIsCreating(false);
    }
  };
  return <AppModal isOpen={isOpen} onClose={onClose} title="Nuova Policy" data-id="element-489">
      <div className="space-y-4" data-id="element-490">
        <AppInput label="Titolo" value={title} onChange={e => setTitle(e.target.value)} placeholder="Es. Nessun dato sensibile" data-id="element-491" />

        <AppTextarea label="Descrizione" value={description} onChange={e => setDescription(e.target.value)} placeholder="Descrivi la policy in dettaglio..." rows={4} data-id="element-492" />

        <div data-id="element-493">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-494">
            Categoria
          </label>
          <select value={category} onChange={e => setCategory(e.target.value as Policy['category'])} className="w-full bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle text-gray-700 dark:text-gray-300 py-2.5 px-3.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" data-id="element-495">
            <option value="privacy" data-id="element-496">Privacy</option>
            <option value="content" data-id="element-497">Contenuti</option>
            <option value="behavior" data-id="element-498">Comportamento</option>
            <option value="compliance" data-id="element-499">Compliance</option>
          </select>
        </div>

        <div data-id="element-500">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-501">
            Severità
          </label>
          <select value={severity} onChange={e => setSeverity(e.target.value as Policy['severity'])} className="w-full bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle text-gray-700 dark:text-gray-300 py-2.5 px-3.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" data-id="element-502">
            <option value="high" data-id="element-503">Alta</option>
            <option value="medium" data-id="element-504">Media</option>
            <option value="low" data-id="element-505">Bassa</option>
          </select>
        </div>

        <div className="flex items-center" data-id="element-506">
          <input type="checkbox" id="enabled" checked={enabled} onChange={e => setEnabled(e.target.checked)} className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" data-id="element-507" />
          <label htmlFor="enabled" className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-508">
            Abilita policy immediatamente
          </label>
        </div>

        <div className="flex gap-3 pt-4" data-id="element-509">
          <AppButton variant="secondary" onClick={onClose} fullWidth data-id="element-510">
            Annulla
          </AppButton>
          <AppButton onClick={handleCreate} fullWidth disabled={isCreating || !title || !description} data-id="element-511">
            {isCreating ? 'Creazione...' : 'Crea policy'}
          </AppButton>
        </div>
      </div>
    </AppModal>;
}
// ============================================================================
// POLICY DELETE MODAL
// ============================================================================
interface PolicyDeleteModalProps {
  isOpen: boolean;
  policy: Policy | null;
  onClose: () => void;
  onDelete: (id: string) => Promise<void>;
}
export function PolicyDeleteModal({
  isOpen,
  policy,
  onClose,
  onDelete
}: PolicyDeleteModalProps) {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const handleDelete = async () => {
    if (!policy) return;
    setIsDeleting(true);
    try {
      await onDelete(policy.id);
      setConfirmText('');
      onClose();
    } catch (error) {
      console.error('Failed to delete policy', error);
    } finally {
      setIsDeleting(false);
    }
  };
  const canDelete = confirmText.toLowerCase() === 'elimina';
  return <AppModal isOpen={isOpen} onClose={onClose} title="Elimina Policy" data-id="element-512">
      <div className="space-y-4" data-id="element-513">
        <div className="flex items-start gap-3 p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-900/30 rounded-xl" data-id="element-514">
          <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" data-id="element-515" />
          <div data-id="element-516">
            <p className="text-sm font-medium text-rose-900 dark:text-rose-200 mb-1" data-id="element-517">
              Attenzione: questa azione è irreversibile
            </p>
            <p className="text-xs text-rose-700 dark:text-rose-300" data-id="element-518">
              Stai per eliminare la policy "{policy?.title}". Questa azione non
              può essere annullata.
            </p>
          </div>
        </div>

        <div data-id="element-519">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-520">
            Per confermare, digita <strong data-id="element-521">ELIMINA</strong>
          </label>
          <input type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="Digita ELIMINA" className="w-full bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle text-gray-700 dark:text-gray-300 py-2.5 px-3.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" data-id="element-522" />
        </div>

        <div className="flex gap-3 pt-4" data-id="element-523">
          <AppButton variant="secondary" onClick={onClose} fullWidth data-id="element-524">
            Annulla
          </AppButton>
          <AppButton onClick={handleDelete} fullWidth disabled={isDeleting || !canDelete} className="bg-rose-600 hover:bg-rose-700 text-white disabled:bg-gray-300 disabled:text-gray-500" data-id="element-525">
            {isDeleting ? 'Eliminazione...' : 'Elimina definitivamente'}
          </AppButton>
        </div>
      </div>
    </AppModal>;
}